import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiCheckCircle, FiCreditCard, FiPlus, FiShield, FiX } from 'react-icons/fi';
import { adsServer } from '../../server';
import { getStoredCurrentProfile } from '../../utils/shopProfile';

const formatBrand = (brand = '') => {
  if (!brand) return 'Card';
  return `${brand.charAt(0).toUpperCase()}${brand.slice(1)}`;
};

const getPaymentMethodTitle = (paymentMethod) => {
  if (paymentMethod.isPlatformTestCard) {
    return 'MATRIX Platform Test Card';
  }

  return `${formatBrand(paymentMethod.brand)} ending ${paymentMethod.last4}`;
};

const getPaymentMethodSubtitle = (paymentMethod) => {
  if (paymentMethod.isPlatformTestCard) {
    return 'Visa ending 4242';
  }

  if (!paymentMethod.exp_month || !paymentMethod.exp_year) {
    return 'Saved payment method';
  }

  return `Expires ${paymentMethod.exp_month}/${paymentMethod.exp_year}`;
};

const AdPaymentMethodSelector = ({ selectedPaymentMethodId, onSelectPaymentMethod, onReadyChange }) => {
  const stripe = useStripe();
  const elements = useElements();
  const currentProfile = getStoredCurrentProfile();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [billingStatus, setBillingStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddCardOpen, setIsAddCardOpen] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState('');

  const requestHeaders = useMemo(() => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'current-profile': currentProfile || 'defaultProfile',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, [currentProfile]);

  const isBillingLocked = billingStatus?.mode === 'live' && billingStatus?.liveBillingAllowed === false;

  const fetchPaymentMethods = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(`${adsServer}/sponsored/payment-methods`, {
        withCredentials: true,
        headers: requestHeaders,
      });

      const methods = data.paymentMethods || [];
      const nextBillingStatus = data.billingStatus || null;
      setPaymentMethods(methods);
      setBillingStatus(nextBillingStatus);

      const selectedStillExists = methods.some((method) => method.id === selectedPaymentMethodId);
      if (!selectedStillExists) {
        const preferredMethod = methods.find((method) => method.isDefault && !method.isPlatformTestCard)
          || methods.find((method) => method.isDefault)
          || methods[0];

        onSelectPaymentMethod(preferredMethod?.id || '');
      }
    } catch (error) {
      console.error('Error loading payment methods:', error);
      toast.error(error.response?.data?.message || 'Unable to load payment methods.');
      setPaymentMethods([]);
      onSelectPaymentMethod('');
    } finally {
      setIsLoading(false);
    }
  }, [onSelectPaymentMethod, requestHeaders, selectedPaymentMethodId]);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  useEffect(() => {
    const selectedMethod = paymentMethods.find((method) => method.id === selectedPaymentMethodId);
    onReadyChange(Boolean(selectedMethod) && !isBillingLocked);
  }, [isBillingLocked, onReadyChange, paymentMethods, selectedPaymentMethodId]);

  const openAddCard = async () => {
    setIsAddCardOpen(true);
    setSetupClientSecret('');

    try {
      const { data } = await axios.post(`${adsServer}/sponsored/payment-methods/setup-intent`, {}, {
        withCredentials: true,
        headers: requestHeaders,
      });
      setSetupClientSecret(data.clientSecret || '');
    } catch (error) {
      console.error('Error creating setup intent:', error);
      toast.error(error.response?.data?.message || 'Unable to start payment method setup.');
      setIsAddCardOpen(false);
    }
  };

  const handleAddCard = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !setupClientSecret) {
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      toast.error('Card form is not ready yet.');
      return;
    }

    setIsSaving(true);

    try {
      const { error, setupIntent } = await stripe.confirmCardSetup(setupClientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (error) {
        toast.error(error.message || 'Unable to save payment method.');
        return;
      }

      const paymentMethodId = typeof setupIntent.payment_method === 'string'
        ? setupIntent.payment_method
        : setupIntent.payment_method?.id;

      if (!paymentMethodId) {
        toast.error('Stripe did not return a saved payment method.');
        return;
      }

      await axios.post(`${adsServer}/sponsored/payment-methods/default`, {
        paymentMethodId,
      }, {
        withCredentials: true,
        headers: requestHeaders,
      });

      setIsAddCardOpen(false);
      onSelectPaymentMethod(paymentMethodId);
      await fetchPaymentMethods();
      toast.success('Payment method saved.');
    } catch (error) {
      console.error('Error saving payment method:', error);
      toast.error(error.response?.data?.message || 'Unable to save payment method.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="ad-payment-method-selector">
      <div className="ad-payment-method-selector-header">
        <div>
          <h4>Payment Method</h4>
          {billingStatus?.canUsePlatformTestCard && (
            <span className="ad-payment-method-mode-badge">
              <FiShield /> Stripe Test Mode
            </span>
          )}
        </div>
        <button
          type="button"
          className="ad-payment-method-add-button"
          onClick={openAddCard}
          disabled={isBillingLocked || isLoading}
        >
          <FiPlus /> Add Payment Method
        </button>
      </div>

      {isBillingLocked && (
        <div className="ad-payment-method-alert">
          Ad billing is locked because live Stripe billing is not enabled for ads.
        </div>
      )}

      <div className="ad-payment-method-list">
        {isLoading ? (
          <div className="ad-payment-method-loading">Loading payment methods...</div>
        ) : paymentMethods.length > 0 ? (
          paymentMethods.map((paymentMethod) => {
            const isSelected = selectedPaymentMethodId === paymentMethod.id;

            return (
              <button
                key={paymentMethod.id}
                type="button"
                className={`ad-payment-method-option ${isSelected ? 'selected' : ''} ${paymentMethod.isPlatformTestCard ? 'platform-test' : ''}`}
                onClick={() => onSelectPaymentMethod(paymentMethod.id)}
                disabled={isBillingLocked}
              >
                <span className="ad-payment-method-icon">
                  {isSelected ? <FiCheckCircle /> : <FiCreditCard />}
                </span>
                <span className="ad-payment-method-copy">
                  <span className="ad-payment-method-title">{getPaymentMethodTitle(paymentMethod)}</span>
                  <span className="ad-payment-method-subtitle">{getPaymentMethodSubtitle(paymentMethod)}</span>
                </span>
                <span className="ad-payment-method-badges">
                  {paymentMethod.isDefault && <span>Default</span>}
                  {paymentMethod.isPlatformTestCard && <span>Platform Admin Only</span>}
                </span>
              </button>
            );
          })
        ) : (
          <div className="ad-payment-method-empty">No payment methods on file.</div>
        )}
      </div>

      {isAddCardOpen && (
        <div className="ad-payment-method-modal">
          <div className="ad-payment-method-modal-content">
            <button
              type="button"
              className="ad-payment-method-modal-close"
              onClick={() => setIsAddCardOpen(false)}
              aria-label="Close"
            >
              <FiX />
            </button>
            <h4>Add Payment Method</h4>
            <form onSubmit={handleAddCard} className="ad-payment-method-form">
              <div className="ad-payment-method-card-element">
                <CardElement />
              </div>
              <button
                type="submit"
                className="ad-payment-method-save-button"
                disabled={!stripe || !setupClientSecret || isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Payment Method'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdPaymentMethodSelector;